import { useState } from 'react';
import { Drawer, Empty, Spin, Button, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api, type MediaRow } from '../../api/client';
import MediaFilterBar, { type MediaFilters } from '../../pages/media/MediaFilterBar';
import PickerGrid from './PickerGrid';
import MediaPager from '../../pages/media/MediaPager';
import { useDrawerWidth } from '../../hooks/useIsMobile';

const PAGE_SIZE = 24;

function toParams(f: MediaFilters, page: number): Record<string, string> {
  const p: Record<string, string> = { page: String(page), pageSize: String(PAGE_SIZE) };
  if (f.q) p.q = f.q;
  if (f.tagId) p.tagId = f.tagId;
  if (f.categoryId) p.categoryId = f.categoryId;
  if (f.dateFrom) p.dateFrom = String(f.dateFrom);
  if (f.dateTo) p.dateTo = String(f.dateTo);
  return p;
}

/** 已選提示：讓記者確認自己點到的是哪一張。 */
function pickedLabel(picked: MediaRow | null): string {
  if (!picked) return '點一張照片後按「使用這張」';
  return `已選：${picked.title ?? picked.url.split('/').pop() ?? ''}`;
}

interface Props {
  open: boolean;
  title?: string;
  onClose: () => void;
  onPick: (item: MediaRow) => void;
}

/**
 * 從媒體庫挑一張既有的照片（封面、內文插圖共用）。用 Drawer 從右側滑出。
 * 篩選條件與媒體庫同一套（關鍵字／標籤／分類／刊出日），記者才不用換一套操作邏輯。
 */
export default function MediaPickerDrawer({ open, title = '從媒體庫挑選', onClose, onPick }: Props) {
  const [f, setF] = useState<MediaFilters>({});
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<MediaRow | null>(null);
  const width = useDrawerWidth('min(1100px, 92vw)');

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories, enabled: open });
  const params = toParams(f, page);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['media', params],
    queryFn: () => api.mediaList(params),
    enabled: open,
    placeholderData: (prev) => prev,
  });

  const patch = (p: Partial<MediaFilters>): void => {
    setF((s) => ({ ...s, ...p }));
    setPage(1);
  };
  const close = (): void => {
    setPicked(null);
    onClose();
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const categoryOptions = (categories.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  return (
    <Drawer
      title={title}
      open={open}
      onClose={close}
      width={width}
      // 手機：內距縮小，讓格線／表格多一點空間
      classNames={{ body: 'max-md:!p-3' }}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <span className="min-w-0 flex-1 truncate text-xs text-gray-400">{pickedLabel(picked)}</span>
          <Space className="ml-auto">
            <Button onClick={close}>取消</Button>
            <Button
              type="primary"
              disabled={!picked}
              onClick={() => {
                if (picked) onPick(picked);
                close();
              }}
            >
              使用這張
            </Button>
          </Space>
        </div>
      }
    >
      <MediaFilterBar categories={categoryOptions} onChange={patch} />

      {isLoading ? (
        <Spin className="block py-20 text-center" />
      ) : items.length === 0 ? (
        <Empty description="找不到符合條件的照片" />
      ) : (
        <div className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
          <PickerGrid items={items} pickedId={picked?.id ?? null} onPick={setPicked} />
          <MediaPager className="mt-4 text-center" current={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
        </div>
      )}
    </Drawer>
  );
}
